




































































import { Component, Prop, Vue } from 'vue-property-decorator';
import {
  IGetBookingDetailResponse,
  getBookingDetail,
  IGetBookingDetailRequest,
} from '@/api/booking';
import BookingDetailInfo from '@/components/booking/detail/Info.vue';
import BookingDetailTickets from '@/components/booking/detail/Tickets.vue';
import BookingDetailIssuingLogs from '@/components/booking/detail/IssuingLogs.vue';
import { AppModule } from '@/store/modules/app';
import { UserModule } from '@/store/modules/user';
import { formatApiError, translateViewResource } from '@/utils/index';
import { getParticipantName } from '@/utils/tickets';
import {
  IssuedStatusEnum,
  IUserTokenData,
  BookingStatusEnum,
  IssuingRecordActionEnum,
  PlanSettingPrintSpecType,
} from '@/api/types';
import { BookingModule, IssueTicketReceipt } from '@/store/modules/booking';
import { strDateTime } from '@/utils/date';

@Component({
  name: 'BookingDetail',
  components: {
    BookingDetailInfo,
    BookingDetailTickets,
    BookingDetailIssuingLogs,
  },
})
export default class extends Vue {
  @Prop({ required: true }) bookingId!: string;
  private data = {} as IGetBookingDetailResponse;
  private loading = true;
  private dialog = false;
  private dialogText = '';

  get ticketsUpdatedEvent() {
    return BookingDetailTickets.ticketsUpdatedEvent;
  }

  mounted() {
    this.fetchData(true).then(() => {
      this.showDialog();
    });
  }

  private async fetchData(initial = false) {
    this.loading = true;
    AppModule.StartProgress();
    try {
      this.data = await getBookingDetail({
        bookingId: this.bookingId,
      } as IGetBookingDetailRequest);
      this.postFetchData(initial);
      AppModule.StopProgress();
      this.loading = false;
    } catch (err) {
      AppModule.StopProgress();
      this.loading = false;
      this.$toast.error(
        this.$t('Failed to {do} due to {err}', {
          do: (this.$t(`@route.Booking detail`) as string).toLowerCase(),
          err: formatApiError(this.$i18n, err),
        }) as string,
        {
          icon: 'mdi-alert',
        }
      );
    }
  }

  private postFetchData(initial: boolean) {
    // beep
    if (
      initial &&
      this.data.uiSetting &&
      this.data.uiSetting.bookingFormBeepOnOpen
    ) {
      this.beep();
    }
  }

  private beep() {
    var snd = new Audio(
      'data:audio/wav;base64,UklGRl9vT19XQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YU' +
        Array(1e3).join('123')
    );
    snd.play();
  }

  private showDialog() {
    if (this.isBookingUnavailable()) {
      this.dialogText = 'Unavailable booking';
      this.dialog = true;
    } else if (this.isAllIssued()) {
      this.dialogText = 'All issued';
      this.dialog = true;
    } else if (this.lockedByOther()) {
      this.dialogText = 'Locked by other user';
      this.dialog = true;
    } else if (this.noIssuableTickets()) {
      this.dialogText = 'No issuable tickets';
      this.dialog = true;
    }
  }

  private resetDialog() {
    this.dialog = false;
    this.$nextTick(() => {
      this.dialogText = '';
    });
  }

  private isBookingUnavailable() {
    return (
      this.data &&
      this.data.bookingStatus &&
      (this.data.bookingStatus as BookingStatusEnum) !==
        BookingStatusEnum.CONFIRMED
    );
  }

  private isAllIssued() {
    return (
      this.data &&
      this.data.issuedStatus &&
      (this.data.issuedStatus as IssuedStatusEnum) === IssuedStatusEnum.ISSUED
    );
  }

  private lockedByOther() {
    const accountId = (JSON.parse(UserModule.token) as IUserTokenData)
      .accountId;
    return (
      this.data &&
      this.data.lock &&
      this.data.lock.isActive &&
      this.data.lock.accountId !== accountId
    );
  }

  private noIssuableTickets() {
    let count = 0;
    if (this.data && this.data.tickets && this.data.tickets.length > 0) {
      this.data.tickets.forEach(t => {
        t.issuableTickets &&
          t.issuableTickets.forEach(i => (count += i.amount));
      });
    }
    return count === 0;
  }

  private async handleTicketsUpdated() {
    await this.fetchData();
    this.handlePrintIssueTicketReceipt();
  }

  private handlePrintIssueTicketReceipt() {
    if (
      this.data.issuingLogs &&
      this.data.issuingLogs.length &&
      this.canPrintIssueTicketReceipt()
    ) {
      const firstLog = this.data.issuingLogs[0];
      const printSpec = (this.data.printSpecs || []).find(
        ps =>
          (ps.type as PlanSettingPrintSpecType) ===
            PlanSettingPrintSpecType.REPRINT_ISSUED_RECEIPT && ps.autoPrint
      );
      if (!printSpec) {
        return;
      }

      const receipt = new IssueTicketReceipt();
      receipt.supplierName = this.data.booking.supplierName || '';
      receipt.supplierId = this.data.booking.supplierId || '';
      receipt.accountName = UserModule.name;
      receipt.bookingId = this.data.booking.bookingId || '';
      receipt.activityName = this.data.booking.activityTitle || '';
      receipt.planName = this.data.booking.planTitle || '';
      receipt.participantName = getParticipantName(
        this.data.booking.participantFirstName || '',
        this.data.booking.participantLastName || '',
        AppModule.language
      );
      receipt.tickets = (firstLog.operatedTickets || []).map(t => ({
        ticketName: t.title,
        ticketCount: t.amount,
      }));
      receipt.issuedTime = strDateTime(firstLog.operatedAt);
      receipt.autoPrint = printSpec.autoPrint;
      BookingModule.SaveBookingIssueTicketReceipt(receipt);

      (this.$refs['receiptForm'] as HTMLFormElement).submit();
    }
  }

  private canPrintIssueTicketReceipt() {
    if (this.data.issuingLogs && this.data.issuingLogs.length) {
      const firstLog = this.data.issuingLogs[0];
      if (
        (firstLog.action as IssuingRecordActionEnum) ===
        IssuingRecordActionEnum.ISSUE
      ) {
        return (this.data.printSpecs || []).some(
          ps =>
            (ps.type as PlanSettingPrintSpecType) ===
              PlanSettingPrintSpecType.REPRINT_ISSUED_RECEIPT && ps.autoPrint
        );
      }
    }
    return false;
  }

  private translateViewResource(resource: string) {
    return translateViewResource(
      this.$i18n,
      this.$options.name as string,
      resource
    );
  }
}
